import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from '@hooks/core/useCustomQuery';
import { QueryKeys } from '@constants/queryKeys';

export const SPLIT_DATA_QUERY_KEY = [QueryKeys.SPLIT_DATA];

export type SplitDataState = {
  isSplitEs37823: boolean;
  isSplitEs38606: boolean;
  isSplitEs37975: boolean;
  isSplitEs38751: boolean;
  isSplitEs38799: boolean;
  isSplitEs38934: boolean;
  isSplitEs39123: boolean;
  isSplitEs39088: boolean;
  isSplitEs39290: boolean;
  isSplitEs39198: boolean;
  isSplitEs39195: boolean;
  // Picks the whole humanizer / detector screen: null until userData lands.
  isSplitEs39222: boolean | null;
};

const initialSplitDataState: SplitDataState = {
  isSplitEs37823: false,
  isSplitEs38606: false,
  isSplitEs37975: false,
  isSplitEs38751: false,
  isSplitEs38799: false,
  isSplitEs38934: false,
  isSplitEs39123: false,
  isSplitEs39088: false,
  isSplitEs39290: false,
  isSplitEs39198: false,
  isSplitEs39195: false,
  isSplitEs39222: null,
};

// Split flags live in the query cache (single source of truth), mirroring the
// useOnboardingTooltips pattern. The query never hits the network — it returns
// whatever is already cached; writes go through setSplitData.
export const useSplitData = () => {
  const queryClient = useQueryClient();

  const { data: splitData = initialSplitDataState } =
    useCustomQuery<SplitDataState>(
      SPLIT_DATA_QUERY_KEY,
      () => queryClient.getQueryData(SPLIT_DATA_QUERY_KEY) as SplitDataState,
      {
        initialData: initialSplitDataState,
        staleTime: Infinity,
        enabled: true,
      }
    );

  const setSplitData = useCallback(
    (partial: Partial<SplitDataState>) => {
      queryClient.setQueryData<SplitDataState>(
        SPLIT_DATA_QUERY_KEY,
        (prevData) => {
          const base = prevData ?? initialSplitDataState;
          const next = { ...base, ...partial };

          // Return the same reference when nothing changed so idempotent syncs
          // (e.g. from every useUserData instance) don't re-render subscribers.
          const hasChanged = (
            Object.keys(partial) as Array<keyof SplitDataState>
          ).some((key) => base[key] !== next[key]);

          return hasChanged ? next : base;
        }
      );
    },
    [queryClient]
  );

  return {
    ...splitData,
    setSplitData,
  };
};
